import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Wand2, 
  Plus,
  Trash2,
  Search,
  Tag,
  Loader2,
  CheckCircle2,
  Info
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  "Groceries", "Dining", "Transport", "Utilities", "Entertainment",
  "Shopping", "Health", "Subscriptions", "Travel", "Insurance",
  "Personal Care", "Education", "Gifts", "Other"
];

const CATEGORY_COLORS = {
  Groceries: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  Dining: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  Transport: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  Utilities: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  Entertainment: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  Shopping: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400",
  Health: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  Subscriptions: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
  Travel: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
  Insurance: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400",
  "Personal Care": "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
  Education: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
  Gifts: "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-400",
  Other: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
};

export const TransactionRulesPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRule, setNewRule] = useState({ merchant_pattern: "", category: "", rename_to: "" });
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/transaction-rules");
      setRules(response.data);
    } catch (error) {
      console.error("Failed to fetch rules:", error);
      toast.error("Failed to load rules");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleCreateRule = async () => {
    if (!newRule.merchant_pattern || !newRule.category) {
      toast.error("Please fill merchant pattern and category");
      return;
    }
    
    setCreating(true);
    try {
      const response = await api.post("/transaction-rules", {
        merchant_pattern: newRule.merchant_pattern,
        category: newRule.category,
        rename_to: newRule.rename_to || null
      });
      toast.success(`Rule created! Applied to ${response.data.matches_count} transactions`);
      setShowCreateModal(false);
      setNewRule({ merchant_pattern: "", category: "", rename_to: "" });
      fetchRules();
    } catch (error) {
      toast.error("Failed to create rule");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    setDeleting(ruleId);
    try {
      await api.delete(`/transaction-rules/${ruleId}`);
      toast.success("Rule deleted");
      setRules(prev => prev.filter(r => r.id !== ruleId));
    } catch (error) {
      toast.error("Failed to delete rule");
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="rules-loading">
        <div className="flex justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="space-y-3">
          {[1,2,3].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="transaction-rules-page">
      {/* Header */}
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight">Transaction Rules</h1>
          <p className="text-muted-foreground mt-1">
            Auto-categorize transactions by merchant name
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="gap-2 rounded-full" data-testid="create-rule-btn">
          <Plus className="w-4 h-4" />
          New Rule
        </Button>
      </header>

      {/* Info Card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-foreground">How rules work</p>
            <p className="text-muted-foreground mt-1">
              Rules automatically categorize transactions when the merchant name matches your pattern. 
              For example, a rule for &quot;Tim Hortons&quot; will categorize all Tim Hortons transactions.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Rules List */}
      {rules.length === 0 ? (
        <Card className="border-border/50">
          <CardContent className="empty-state">
            <Wand2 className="empty-state-icon" />
            <h3 className="empty-state-title">No rules yet</h3>
            <p className="empty-state-description">
              Create your first rule to automatically categorize transactions.
            </p>
            <Button onClick={() => setShowCreateModal(true)} className="rounded-full">
              <Plus className="mr-2 w-4 h-4" />
              Create your first rule
            </Button>
          </CardContent>
        </Card>
      ) : (
        <section className="space-y-3">
          {rules.map((rule) => (
            <Card key={rule.id} className="border-border/50" data-testid={`rule-${rule.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Search className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                          {rule.merchant_pattern}
                        </code>
                        <span className="text-muted-foreground">→</span>
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${CATEGORY_COLORS[rule.category] || CATEGORY_COLORS.Other}`}>
                          {rule.category}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-sm text-muted-foreground">
                        {rule.rename_to && (
                          <span className="flex items-center gap-1">
                            <Tag className="w-3 h-3" />
                            Rename to: {rule.rename_to}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          {rule.matches_count} matches
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteRule(rule.id)}
                    disabled={deleting === rule.id}
                    className="text-muted-foreground hover:text-destructive min-h-[44px] min-w-[44px]"
                    aria-label="Delete rule"
                    data-testid={`delete-rule-${rule.id}`}
                  >
                    {deleting === rule.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </section>
      )}

      {/* Create Rule Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Transaction Rule</DialogTitle>
            <DialogDescription>
              Set up automatic categorization for transactions
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Merchant Pattern</Label>
              <Input
                placeholder="e.g., Tim Hortons, Uber, Amazon"
                value={newRule.merchant_pattern}
                onChange={(e) => setNewRule(prev => ({ ...prev, merchant_pattern: e.target.value }))}
                data-testid="rule-pattern-input"
              />
              <p className="text-xs text-muted-foreground">
                Matches any merchant containing this text (case insensitive)
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Category</Label>
              <Select 
                value={newRule.category} 
                onValueChange={(v) => setNewRule(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger data-testid="rule-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Rename To (Optional)</Label>
              <Input
                placeholder="e.g., Coffee, Ride Share"
                value={newRule.rename_to}
                onChange={(e) => setNewRule(prev => ({ ...prev, rename_to: e.target.value }))}
                data-testid="rule-rename-input"
              />
              <p className="text-xs text-muted-foreground">
                Display a cleaner name for matched transactions
              </p>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button className="flex-1 rounded-full" onClick={handleCreateRule} disabled={creating} data-testid="save-rule-btn">
                {creating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Rule"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
